/**
 * BookPersonalizationMessage Component
 * 
 * Displays AI-generated personalized message with:
 * - Sparkles icon
 * - Typewriter animation on first view
 * - One-time animation per book per session
 */

import React, { useState, useEffect, useRef } from 'react';
import { View, StyleSheet, Animated } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Text from './Text';
import { Theme } from '../../utils/theme';

export interface BookPersonalizationMessageProps {
  bookId: string;
  message: string;
  shouldAnimate: boolean; // Whether to animate (based on tracking hook)
  delayMs?: number; // Optional delay before starting animation (for staggered effect)
  onAnimationComplete?: () => void;
}

const BookPersonalizationMessage: React.FC<BookPersonalizationMessageProps> = ({
  bookId,
  message,
  shouldAnimate,
  delayMs = 0,
  onAnimationComplete,
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const iconOpacity = useRef(new Animated.Value(0)).current;
  const messageRef = useRef(message);

  // Typewriter effect - only runs once per book
  useEffect(() => {
    // If already animated, keep showing full text
    if (hasAnimated) {
      setDisplayedText(message);
      return;
    }

    // Store message reference
    messageRef.current = message;

    // If shouldn't animate, show full text immediately
    if (!shouldAnimate) {
      setDisplayedText(message);
      setHasAnimated(true);
      Animated.timing(iconOpacity, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start();
      return;
    }

    // Wait for staggered delay, then start animation
    const initialDelay = setTimeout(() => {
      // Start animation
      setIsAnimating(true);
      setDisplayedText('');
      
      // Fade in icon
      Animated.timing(iconOpacity, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start();

      let currentIndex = 0;
      const intervalTime = 2000 / message.length; // Complete animation in ~2 seconds
      
      const typeNextChar = () => {
        if (currentIndex < messageRef.current.length) {
          setDisplayedText(messageRef.current.substring(0, currentIndex + 1));
          currentIndex++;
          animationTimeoutRef.current = setTimeout(typeNextChar, intervalTime);
        } else {
          // Animation complete - keep full text displayed
          setDisplayedText(messageRef.current);
          setIsAnimating(false);
          setHasAnimated(true);
          onAnimationComplete?.();
        }
      };

      // Start typing after icon fades in
      animationTimeoutRef.current = setTimeout(typeNextChar, 300);
    }, delayMs); // Staggered delay based on position

    // Cleanup
    return () => {
      clearTimeout(initialDelay);
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [shouldAnimate, hasAnimated]); // Only depend on shouldAnimate and hasAnimated

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Animated.View style={[styles.iconContainer, { opacity: iconOpacity }]}>
          <Ionicons name="sparkles" size={16} color={Theme.colors.primary} />
        </Animated.View>
        <Text style={styles.label}>For You</Text>
      </View>
      <Text style={styles.message}>
        {displayedText}
        {isAnimating && <Text style={styles.cursor}>▋</Text>}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingTop: Theme.spacing.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.xs,
    marginBottom: Theme.spacing.xs,
  },
  iconContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  label: {
    fontSize: 11,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  message: {
    fontSize: 13,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
    lineHeight: 20,
  },
  cursor: {
    color: Theme.colors.primary,
    fontFamily: Theme.typography.fontFamily.regular,
  },
});

export default BookPersonalizationMessage;

